<template>
    <div>
        <!-- Hidden field: its value is the JSON string of the full phone object -->
        <input
            type="hidden"
            :name="storeInArray ? `formData${storeInArray}[${currentField.tagname}]` : `formData[${currentField.tagname}]`"
            :value="serializedInput"
        />

        <b-input-group :prepend="currentField.prepend || ''" :append="currentField.append || ''">
            <vue-tel-input
                :validCharactersOnly="true"
                inputClasses="form-control is-invalid"
                wrapperClasses="phone-input-wrapper"
                style="width: 100%"
                @validate="onTelInputValidate"
                v-model="phoneDisplay"
                @input="onTelInput"
                :mode="mode"
                :disabledFormatting="disabledFormatting"
                :enabledCountryCode="true"
                :enabledFlags="true"
                :dropdownOptions="dropdownOptions"
                :defaultCountry="input && typeof input === 'object' && input.countryCode ? input.countryCode : defaultCountry"
                :inputOptions="inputOptions"
                ref="telInput"
            />
            <span class="invalid-feedback" style="display: inline-block; color: red" role="alert" v-if="error">{{ error }}</span>
        </b-input-group>
    </div>
</template>

<script>
import {VueTelInput} from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'

export default {
    name: 'PhoneInput',
    components: {
        VueTelInput
    },
    props: ['currentField', 'storeInArray', 'value', 'error'],
    data() {
        return {
            // For debugging
            debug: true,

            // Initialize the internal value by parsing incoming value if needed.
            input: this.parseInput(this.value),
            // Options for vue-tel-input:
            mode: 'national',
            disabledFormatting: true,
            defaultCountry: 'auto',
            // Default set to auto, but will use countryCode from saved data if available
            dropdownOptions: {
                showDialCodeInList: true,
                showDialCodeInSelection: true,
                showFlags: true,
                showSearchBox: true
            },
            inputOptions: {
                autocomplete: 'off',
                showDialCode: false,
                placeholder: this.currentField.isPlaceholderVisible ? (this.currentField.placeholder || '') : '',
                // Try setting class directly in inputOptions
                class: 'form-control'
            },
            // Flag to track if the component has been initialized
            isInitialized: false
        }
    },
    mounted() {
        // Mark as initialized after the component is mounted
        this.$nextTick(() => {
            this.isInitialized = true;

            // Try direct DOM manipulation as a last resort
            this.applyStylesDirectly();
        });
    },
    computed: {
        // For debugging
        inputClassesComputed() {
            return this.error ? 'form-control is-invalid' : 'form-control';
        },

        /**
         * This computed property is bound to the vue-tel-input.
         * It ALWAYS returns the national number, never the full international number
         */
        phoneDisplay: {
            get() {
                if (
                    this.input &&
                    typeof this.input === 'object'
                ) {
                    // Always use nationalNumber for display
                    if (this.input.nationalNumber) {
                        return this.input.nationalNumber.replace(/\s+/g, '');
                    }
                    // If nationalNumber is missing but we have a number and country code,
                    // try to extract the national part
                    if (this.input.number && this.input.countryCallingCode) {
                        const fullNumber = this.input.number;
                        const countryCode = this.input.countryCallingCode;

                        if (fullNumber.startsWith('+' + countryCode)) {
                            return fullNumber.substring(('+' + countryCode).length).trim();
                        } else if (fullNumber.startsWith(countryCode)) {
                            return fullNumber.substring(countryCode.length).trim();
                        }
                    }
                }
                return typeof this.input === 'string' ? this.input : '';
            },
            set(newVal) {
                if (this.input && typeof this.input === 'object') {
                    // When setting a new value, update nationalNumber
                    // and rebuild the full number with country code
                    this.input = {
                        ...this.input,
                        nationalNumber: newVal,
                        number: this.input.countryCallingCode ?
                            `+${this.input.countryCallingCode}${newVal}` : newVal
                    };
                } else {
                    this.input = newVal;
                }
            }
        },
        /**
         * This computed property produces the JSON string for the hidden field.
         */
        serializedInput() {
            if (!this.input) return '';
            try {
                return JSON.stringify(this.input);
            } catch (err) {
                console.error('Could not stringify phone object:', err);
                return '';
            }
        }
    },
    watch: {
        // If the parent value changes, re-parse it.
        value(newVal) {
            this.input = this.parseInput(newVal);
        },
        // Whenever our internal value changes, emit the updated event.
        input(newVal) {
            this.$emit('updated', {
                key: this.currentField.tagname,
                value: newVal
            });
        },
        // Watch for error changes and apply styles directly
        error() {
            this.$nextTick(() => {
                this.applyStylesDirectly();
            });
        }
    },
    methods: {
        // Direct DOM manipulation as a last resort
        applyStylesDirectly() {
            try {
                const input = this.$el.querySelector('.vti__input');
                if (input) {
                    input.classList.add('form-control');
                    if (this.error) {
                        input.classList.add('is-invalid');
                    } else {
                        input.classList.remove('is-invalid');
                    }
                }

                const dropdown = this.$el.querySelector('.vti__dropdown');
                if (dropdown) {
                    dropdown.style.marginRight = '10px';
                    if (this.error) {
                        dropdown.style.borderColor = '#dc3545';
                    } else {
                        dropdown.style.borderColor = '';
                    }
                }
            } catch (e) {
                console.error('Error applying styles directly:', e);
            }
        },

        onTelInputValidate(isValid) {
            // Update validation status only if we have input data
            if (this.input && typeof this.input === 'object' && this.phoneDisplay) {
                this.input = {
                    ...this.input,
                    valid: isValid,
                    isValid: isValid
                };
            }
        },
        /**
         * Parse the incoming value.
         * If it's a JSON string, convert it to an object.
         */
        parseInput(val) {
            if (typeof val === 'string') {
                try {
                    const parsed = JSON.parse(val);
                    if (parsed && typeof parsed === 'object') {
                        return parsed;
                    }
                } catch (e) {
                    return val;
                }
            }
            return val;
        },
        /**
         * The vue-tel-input @input event passes (phoneNumber, phoneObject).
         * We capture the full phone object in our internal "input".
         */
        onTelInput(phoneNumber, phoneObject) {
            // Skip empty updates during initialization which would wipe out our data
            if (!this.isInitialized || !phoneObject) {
                return;
            }

            // Skip if phoneObject has empty/null properties and we already have valid data
            const hasEmptyProperties = !phoneObject.nationalNumber &&
                                      (!phoneObject.number || phoneObject.number === '+');

            if (hasEmptyProperties &&
                this.input &&
                typeof this.input === 'object' &&
                this.input.nationalNumber) {
                return;
            }

            // Only merge objects if we have valid data in phoneObject
            const plainObject = {
                ...this.input,
                number: phoneObject.number,
                isValid: phoneObject.isValid,
                valid: phoneObject.isValid,
                country: phoneObject.country,
                countryCode: phoneObject.countryCode,
                nationalNumber: phoneObject.nationalNumber,
                formatted: phoneObject.formatted,
                countryCallingCode: phoneObject.countryCallingCode
            };

            this.input = plainObject;

            // Apply styles after input changes
            this.$nextTick(() => {
                this.applyStylesDirectly();
            });
        }
    }
}
</script>
<style>
/* Target the direct child elements */
.vti__dropdown {
    flex: 0 0 auto;
    margin-right: 10px !important;
}

/* Let the number input take the remaining space; never let it collapse to nothing */
.vti__input {
    flex: 1 1 auto;
    min-width: 80px;
}

form .group--form .vti__dropdown {
    border-radius: 0;
    border: 1px solid #0a232d;
    box-shadow: none;
    height: 50px!important;
    padding: 0 15px;
    color: #000;
    font-size: 15px;
    font-weight: 400;
}

/* When there's an error */
:deep(.is-invalid),
:deep(.is-invalid ~ .vti__dropdown) {
    border-color: #dc3545 !important;
}

/* Ensure the error message shows properly */
.invalid-feedback {
    display: block !important;
}

.vue-tel-input {
    border: none!important;
}
</style>
